/**
 * QR Code encoder — byte mode, error-correction level M, versions 1–10.
 *
 * P2-07 only needs to draw a table tent, and the dashboard ships no QR
 * dependency, so the encoder lives here rather than in package.json. Version 10
 * holds 213 bytes; a `https://…/scan?t=<22 chars>` URL is around 45, so the
 * table never approaches the ceiling.
 *
 * Reference: ISO/IEC 18004 §6 (encoding) and §7 (symbol construction).
 */

export interface QrMatrix {
  /** Modules per side, excluding the quiet zone. */
  size: number;
  /** `true` is a dark module, indexed `[row][col]`. */
  modules: boolean[][];
  version: number;
}

/** Per version: [ecCodewordsPerBlock, group1Blocks, group1Data, group2Blocks, group2Data]. */
const EC_BLOCKS_M: readonly (readonly number[])[] = [
  [10, 1, 16, 0, 0],
  [16, 1, 28, 0, 0],
  [26, 1, 44, 0, 0],
  [18, 2, 32, 0, 0],
  [24, 2, 43, 0, 0],
  [16, 4, 27, 0, 0],
  [18, 4, 31, 0, 0],
  [22, 2, 38, 2, 39],
  [22, 3, 36, 2, 37],
  [26, 4, 43, 1, 44],
];

/** Alignment-pattern centre coordinates per version (empty for version 1). */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

/** Unused bits after the last codeword; versions 2–6 carry seven. */
const REMAINDER_BITS = [0, 7, 7, 7, 7, 7, 0, 0, 0, 0];

const MAX_VERSION = EC_BLOCKS_M.length;

// ---------------------------------------------------------------------------
// GF(256) arithmetic for Reed–Solomon
// ---------------------------------------------------------------------------

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);

(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d; // the QR primitive polynomial
  }
  for (let i = 255; i < 512; i += 1) GF_EXP[i] = GF_EXP[i - 255];
})();

function gfMul(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

/** Product of (x − α⁰)…(x − α^(degree−1)), highest power first. */
function generatorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function ecCodewords(data: readonly number[], count: number): number[] {
  const gen = generatorPoly(count);
  const remainder = new Array<number>(count).fill(0);
  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    for (let i = 0; i < count; i += 1) remainder[i] ^= gfMul(gen[i + 1], factor);
  }
  return remainder;
}

// ---------------------------------------------------------------------------
// Data encoding
// ---------------------------------------------------------------------------

function dataCapacityBytes(version: number): number {
  const [, g1, d1, g2, d2] = EC_BLOCKS_M[version - 1];
  const codewords = g1 * d1 + g2 * d2;
  const headerBits = 4 + (version < 10 ? 8 : 16);
  return Math.floor((codewords * 8 - headerBits) / 8);
}

function pickVersion(byteLength: number): number {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    if (byteLength <= dataCapacityBytes(version)) return version;
  }
  throw new RangeError(`${byteLength} bytes does not fit in a version-${MAX_VERSION} QR code`);
}

function encodeCodewords(bytes: Uint8Array, version: number): number[] {
  const [, g1, d1, g2, d2] = EC_BLOCKS_M[version - 1];
  const dataCount = g1 * d1 + g2 * d2;

  const bits: number[] = [];
  const put = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };

  put(0b0100, 4); // byte mode
  put(bytes.length, version < 10 ? 8 : 16);
  for (const byte of bytes) put(byte, 8);

  const capacityBits = dataCount * 8;
  put(0, Math.min(4, capacityBits - bits.length)); // terminator
  while (bits.length % 8 !== 0) bits.push(0);

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j];
    data.push(byte);
  }
  for (let pad = 0; data.length < dataCount; pad += 1) data.push(pad % 2 === 0 ? 0xec : 0x11);

  // Split into blocks, then interleave data and EC codewords (§7.6).
  const blocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  const shape = [
    ...Array.from({ length: g1 }, () => d1),
    ...Array.from({ length: g2 }, () => d2),
  ];
  for (const length of shape) {
    const block = data.slice(offset, offset + length);
    offset += length;
    blocks.push(block);
    ecBlocks.push(ecCodewords(block, EC_BLOCKS_M[version - 1][0]));
  }

  const result: number[] = [];
  const longest = Math.max(...shape);
  for (let i = 0; i < longest; i += 1) {
    for (const block of blocks) if (i < block.length) result.push(block[i]);
  }
  for (let i = 0; i < EC_BLOCKS_M[version - 1][0]; i += 1) {
    for (const block of ecBlocks) result.push(block[i]);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Symbol construction
// ---------------------------------------------------------------------------

const MASKS: readonly ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function formatInfo(mask: number): number {
  const data = (0b00 << 3) | mask; // 0b00 = error-correction level M
  let rem = data << 10;
  for (let i = 14; i >= 10; i -= 1) if ((rem >>> i) & 1) rem ^= 0x537 << (i - 10);
  return ((data << 10) | (rem & 0x3ff)) ^ 0x5412;
}

function versionInfo(version: number): number {
  let rem = version << 12;
  for (let i = 17; i >= 12; i -= 1) if ((rem >>> i) & 1) rem ^= 0x1f25 << (i - 12);
  return (version << 12) | (rem & 0xfff);
}

interface Canvas {
  size: number;
  modules: boolean[][];
  reserved: boolean[][];
}

function blankCanvas(size: number): Canvas {
  return {
    size,
    modules: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}

function drawFunctionPatterns(canvas: Canvas, version: number): void {
  const { size, modules, reserved } = canvas;

  const mark = (row: number, col: number, dark: boolean) => {
    if (row < 0 || col < 0 || row >= size || col >= size) return;
    modules[row][col] = dark;
    reserved[row][col] = true;
  };

  // Finder patterns plus their separators.
  for (const [top, left] of [
    [0, 0],
    [0, size - 7],
    [size - 7, 0],
  ]) {
    for (let r = -1; r <= 7; r += 1) {
      for (let c = -1; c <= 7; c += 1) {
        const inRing = r >= 0 && r <= 6 && c >= 0 && c <= 6;
        const dark =
          inRing &&
          (r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4));
        mark(top + r, left + c, dark);
      }
    }
  }

  // Timing patterns.
  for (let i = 8; i < size - 8; i += 1) {
    mark(6, i, i % 2 === 0);
    mark(i, 6, i % 2 === 0);
  }

  // Alignment patterns, skipping the three that would sit on a finder.
  const centers = ALIGNMENT_CENTERS[version - 1];
  for (const row of centers) {
    for (const col of centers) {
      const onFinder =
        (row === 6 && col === 6) ||
        (row === 6 && col === size - 7) ||
        (row === size - 7 && col === 6);
      if (onFinder) continue;
      for (let r = -2; r <= 2; r += 1) {
        for (let c = -2; c <= 2; c += 1) {
          mark(row + r, col + c, Math.max(Math.abs(r), Math.abs(c)) !== 1);
        }
      }
    }
  }

  // Reserve the format-information strips and the always-dark module. Index 6
  // is skipped: those two cells belong to the timing patterns, not the format.
  for (let i = 0; i <= 8; i += 1) {
    if (i === 6) continue;
    mark(i, 8, false);
    mark(8, i, false);
  }
  for (let i = 0; i < 8; i += 1) {
    mark(8, size - 1 - i, false);
    mark(size - 1 - i, 8, false);
  }
  mark(size - 8, 8, true);

  if (version >= 7) {
    for (let i = 0; i < 18; i += 1) {
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      mark(b, a, false);
      mark(a, b, false);
    }
  }
}

function placeData(canvas: Canvas, codewords: readonly number[], remainderBits: number): void {
  const { size, modules, reserved } = canvas;
  const totalBits = codewords.length * 8 + remainderBits;
  let index = 0;
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the vertical timing column is never a data column
    for (let step = 0; step < size; step += 1) {
      const row = upward ? size - 1 - step : step;
      for (let offset = 0; offset < 2; offset += 1) {
        const col = right - offset;
        if (reserved[row][col]) continue;
        const bit =
          index < codewords.length * 8 &&
          ((codewords[index >> 3] >>> (7 - (index & 7))) & 1) === 1;
        modules[row][col] = index < totalBits ? bit : false;
        index += 1;
      }
    }
    upward = !upward;
  }
}

function applyFormatInfo(canvas: Canvas, mask: number, version: number): void {
  const { size, modules } = canvas;
  const fmt = formatInfo(mask);
  const bit = (i: number) => ((fmt >>> i) & 1) === 1;

  for (let i = 0; i <= 5; i += 1) modules[i][8] = bit(i);
  modules[7][8] = bit(6);
  modules[8][8] = bit(7);
  modules[8][7] = bit(8);
  for (let i = 9; i < 15; i += 1) modules[8][14 - i] = bit(i);
  for (let i = 0; i < 8; i += 1) modules[8][size - 1 - i] = bit(i);
  for (let i = 8; i < 15; i += 1) modules[size - 15 + i][8] = bit(i);
  modules[size - 8][8] = true;

  if (version >= 7) {
    const info = versionInfo(version);
    for (let i = 0; i < 18; i += 1) {
      const dark = ((info >>> i) & 1) === 1;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      modules[b][a] = dark;
      modules[a][b] = dark;
    }
  }
}

/** §7.8.3 — the four penalty rules; the lowest-scoring mask wins. */
function penalty(modules: boolean[][]): number {
  const size = modules.length;
  let score = 0;

  const runScore = (line: boolean[]) => {
    let total = 0;
    let run = 1;
    for (let i = 1; i < line.length; i += 1) {
      if (line[i] === line[i - 1]) {
        run += 1;
      } else {
        if (run >= 5) total += 3 + (run - 5);
        run = 1;
      }
    }
    if (run >= 5) total += 3 + (run - 5);
    return total;
  };

  const FINDER_LIKE = [true, false, true, true, true, false, true, false, false, false, false];
  const matchesAt = (line: boolean[], at: number, pattern: boolean[]) =>
    pattern.every((value, i) => line[at + i] === value);

  for (let i = 0; i < size; i += 1) {
    const row = modules[i];
    const col = modules.map((line) => line[i]);
    score += runScore(row) + runScore(col);

    for (const line of [row, col]) {
      for (let at = 0; at + 11 <= size; at += 1) {
        if (matchesAt(line, at, FINDER_LIKE)) score += 40;
        if (matchesAt(line, at, [...FINDER_LIKE].reverse())) score += 40;
      }
    }
  }

  let dark = 0;
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      if (modules[r][c]) dark += 1;
      if (r + 1 < size && c + 1 < size) {
        const value = modules[r][c];
        if (
          modules[r][c + 1] === value &&
          modules[r + 1][c] === value &&
          modules[r + 1][c + 1] === value
        ) {
          score += 3;
        }
      }
    }
  }

  const ratio = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(ratio - 50) / 5) * 10;
  return score;
}

/** Encodes `text` and returns the finished module matrix. */
export function encodeQr(text: string): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length);
  const codewords = encodeCodewords(bytes, version);
  const size = version * 4 + 17;

  const base = blankCanvas(size);
  drawFunctionPatterns(base, version);
  placeData(base, codewords, REMAINDER_BITS[version - 1]);

  let best: boolean[][] | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask += 1) {
    const candidate: Canvas = {
      size,
      modules: base.modules.map((row) => [...row]),
      reserved: base.reserved,
    };
    for (let r = 0; r < size; r += 1) {
      for (let c = 0; c < size; c += 1) {
        if (!candidate.reserved[r][c] && MASKS[mask](r, c)) {
          candidate.modules[r][c] = !candidate.modules[r][c];
        }
      }
    }
    applyFormatInfo(candidate, mask, version);

    const score = penalty(candidate.modules);
    if (score < bestScore) {
      bestScore = score;
      best = candidate.modules;
    }
  }

  return { size, modules: best as boolean[][], version };
}
